import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ErrorBar,
} from "recharts";
import { TimePeriod } from "./TimePeriodSelector";
import { getDayXAxis, getXAxisTickFormatterForPeriod } from "./axis";
import type { HeartRateResponse } from "@/types/tracker";
import { CHART_COLORS, CHART_CONFIG, getMetricColor } from "./chart-theme";

interface HeartRateChartProps {
  data: HeartRateResponse | null;
  selectedPeriod: TimePeriod;
  currentDate: Date;
  yAxisConfig: { min: number; max: number };
}

export const HeartRateChart = ({
  data,
  selectedPeriod,
  currentDate,
  yAxisConfig,
}: HeartRateChartProps) => {
  const hasData = data && data.data && data.data.length > 0;
  const metricColor = getMetricColor("heart-rate");

  // Calculate Y-axis interval based on range
  const calculateYAxisInterval = (min: number, max: number): number => {
    const range = max - min;
    if (range <= 10) return 2;
    if (range <= 20) return 5;
    if (range <= 50) return 10;
    if (range <= 100) return 20;
    if (range <= 200) return 50;
    if (range <= 500) return 100;
    return 200;
  };

  // Get Y-axis ticks based on calculated interval
  const getYAxisTicks = (min: number, max: number): number[] => {
    const interval = calculateYAxisInterval(min, max);
    const ticks: number[] = [];
    let tick = Math.ceil(min);
    while (tick <= Math.floor(max)) {
      ticks.push(tick);
      tick += interval;
    }
    const last = Math.floor(max);
    if (ticks.length === 0 || ticks[ticks.length - 1] !== last) {
      ticks.push(last);
    }
    return ticks;
  };

  // Transform heart rate data for chart display
  const transformHeartRateDataForChart = () => {
    if (!hasData) return [];

    return data!.data
      .map((reading) => {
        let d: Date;
        try {
          d =
            reading.time.includes("AM") || reading.time.includes("PM")
              ? new Date(`${reading.date} ${reading.time}`)
              : new Date(`${reading.date}T${reading.time}`);
          if (isNaN(d.getTime())) d = new Date(reading.date);
        } catch {
          d = new Date(reading.date);
        }

        return {
          xValue: d.getTime(),
          value: reading.value,
          date: reading.date,
          time: reading.time,
        };
      })
      .sort((a, b) => a.xValue - b.xValue);
  };

  // Create empty chart data structure for week/month/year views when no data
  const createEmptyChartData = () => {
    if (selectedPeriod === "day") return [];

    let allCategories: string[] = [];

    if (selectedPeriod === "week") {
      allCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    } else if (selectedPeriod === "month") {
      const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );
      const daysInMonth = endOfMonth.getDate();
      allCategories = Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString()
      );
    } else if (selectedPeriod === "year") {
      allCategories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    }

    return allCategories.map((key) => ({
      categoryKey: key,
      minValue: null,
      maxValue: null,
      mid: null,
      offsetRange: null,
    }));
  };

  // Create heart rate range data for week/month/year views
  const createHeartRateRangeData = () => {
    if (selectedPeriod === "day" || !hasData) return [];

    let allCategories: string[] = [];

    if (selectedPeriod === "week") {
      allCategories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    } else if (selectedPeriod === "month") {
      const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );
      const daysInMonth = endOfMonth.getDate();
      allCategories = Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString()
      );
    } else if (selectedPeriod === "year") {
      allCategories = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    }

    const getCategoryKey = (dateStr: string, timeStr: string) => {
      let d: Date;
      try {
        d =
          timeStr.includes("AM") || timeStr.includes("PM")
            ? new Date(`${dateStr} ${timeStr}`)
            : new Date(`${dateStr}T${timeStr}`);
        if (isNaN(d.getTime())) d = new Date(dateStr);
      } catch {
        d = new Date(dateStr);
      }
      if (selectedPeriod === "week")
        return d.toLocaleDateString("en-US", { weekday: "short" });
      if (selectedPeriod === "month") return d.getDate().toString();
      if (selectedPeriod === "year")
        return d.toLocaleDateString("en-US", { month: "short" });
      return "";
    };

    const map: Record<string, { min: number; max: number }> = {};
    allCategories.forEach((c) => {
      map[c] = { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY };
    });
    data!.data.forEach((r) => {
      const key = getCategoryKey(r.date, r.time);
      if (!key || !(key in map)) return;
      map[key].min = Math.min(map[key].min, r.value);
      map[key].max = Math.max(map[key].max, r.value);
    });

    const rangeData = allCategories.map((key) => {
      const { min, max } = map[key];
      if (!isFinite(min) || !isFinite(max)) {
        return {
          categoryKey: key,
          minValue: null as any,
          maxValue: null as any,
          mid: null as any,
          offsetRange: null as any,
        };
      }
      const mid = (min + max) / 2;
      const offsetRange: [number, number] = [mid - min, max - mid];
      return {
        categoryKey: key,
        minValue: min,
        maxValue: max,
        mid,
        offsetRange,
      };
    });

    return rangeData;
  };

  const dayXAxis_placeholder = getDayXAxis(currentDate);
  const chartData = hasData
    ? transformHeartRateDataForChart()
    : dayXAxis_placeholder.ticks.map((tick: number) => ({
        xValue: tick,
        value: null,
      }));

  if (selectedPeriod === "day") {
    const dayXAxis = getDayXAxis(currentDate);

    return (
      <ChartContainer className="w-full h-full" config={{}}>
        <LineChart data={chartData} margin={CHART_CONFIG.margin}>
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray={CHART_CONFIG.gridDashArray}
          />
          <XAxis
            type="number"
            dataKey="xValue"
            scale="time"
            domain={dayXAxis.domain}
            axisLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
            tickLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1.5 }}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            height={35}
            ticks={dayXAxis.ticks}
            tickFormatter={dayXAxis.tickFormatter}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={[yAxisConfig.min, yAxisConfig.max]}
            ticks={getYAxisTicks(yAxisConfig.min, yAxisConfig.max)}
            interval={0}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div className="bg-popover border border-border rounded-lg shadow-xl p-3">
                    <p className="text-sm font-medium text-foreground">
                      Heart Rate
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      {data.value} bpm
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.time}
                    </p>
                  </div>
                );
              }
              return null;
            }}
            cursor={{ stroke: CHART_COLORS.axis, strokeWidth: 2 }}
          />
          {hasData && (
            <Line
              type="monotone"
              dataKey="value"
              stroke={metricColor}
              strokeWidth={CHART_CONFIG.strokeWidth}
              dot={{
                fill: metricColor,
                strokeWidth: 0,
                r: CHART_CONFIG.dotRadius,
              }}
              activeDot={{
                r: CHART_CONFIG.activeDotRadius,
                strokeWidth: 0,
                fill: metricColor,
              }}
              connectNulls={false}
            />
          )}
        </LineChart>
      </ChartContainer>
    );
  } else {
    const weekMonthYearData = hasData
      ? createHeartRateRangeData()
      : createEmptyChartData();

    return (
      <ChartContainer className="w-full h-full" config={{}}>
        <LineChart data={weekMonthYearData} margin={CHART_CONFIG.margin}>
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray={CHART_CONFIG.gridDashArray}
          />
          <XAxis
            dataKey="categoryKey"
            axisLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
            tickLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1.5 }}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            height={35}
            interval={0}
            tickFormatter={getXAxisTickFormatterForPeriod(
              selectedPeriod,
              currentDate
            )}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={[yAxisConfig.min, yAxisConfig.max]}
            ticks={getYAxisTicks(yAxisConfig.min, yAxisConfig.max)}
            interval={0}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                if (data.minValue !== null && data.maxValue !== null) {
                  return (
                    <div className="bg-popover border border-border rounded-lg shadow-xl p-3">
                      <p className="text-sm font-medium text-foreground">
                        Heart Rate
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {data.minValue === data.maxValue
                          ? data.minValue
                          : `${data.minValue}-${data.maxValue}`}{" "}
                        bpm
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {data.categoryKey}
                      </p>
                    </div>
                  );
                }
              }
              return null;
            }}
            cursor={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
          />
          {hasData && (
            <>
              <Line
                type="monotone"
                dataKey="mid"
                stroke="transparent"
                strokeWidth={0}
                dot={false}
                activeDot={false}
                connectNulls
              >
                <ErrorBar
                  dataKey="offsetRange"
                  width={0}
                  stroke={metricColor}
                  strokeWidth={6}
                  strokeOpacity={0.35}
                  direction="y"
                />
              </Line>
              <Line
                type="linear"
                dataKey="minValue"
                stroke="transparent"
                strokeWidth={0}
                dot={{
                  r: CHART_CONFIG.dotRadius,
                  strokeWidth: 0,
                  fill: metricColor,
                }}
                activeDot={{
                  r: CHART_CONFIG.activeDotRadius,
                  strokeWidth: 0,
                  fill: metricColor,
                }}
                connectNulls
              />
              <Line
                type="linear"
                dataKey="maxValue"
                stroke="transparent"
                strokeWidth={0}
                dot={{
                  r: CHART_CONFIG.dotRadius,
                  strokeWidth: 0,
                  fill: metricColor,
                }}
                activeDot={{
                  r: CHART_CONFIG.activeDotRadius,
                  strokeWidth: 0,
                  fill: metricColor,
                }}
                connectNulls
              />
            </>
          )}
        </LineChart>
      </ChartContainer>
    );
  }
};
